import { Play, Plus, Save, Trash2, Workflow } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
  createWorkflow,
  deleteWorkflow,
  executeWorkflow,
  listWorkflows,
  updateWorkflow,
  type Workflow as WorkflowType,
} from "@/services/workflows";

const defaultDefinition: WorkflowType["definition"] = {
  trigger: { type: "manual", config: {} },
  nodes: [
    {
      id: "start",
      type: "log",
      config: { message: "工作流开始" },
      next: ["condition"],
    },
    {
      id: "condition",
      type: "condition",
      config: {
        field: "amount",
        operator: ">=",
        value: 10000,
        branches: { true: ["webhook"], false: ["log-small"] },
      },
      next: [],
    },
    {
      id: "webhook",
      type: "webhook",
      config: { url: "https://httpbin.org/post", method: "POST", body: { alert: "大额订单" } },
      next: [],
    },
    {
      id: "log-small",
      type: "log",
      config: { message: "小额订单，自动通过" },
      next: [],
    },
  ],
};

export function WorkflowsPage() {
  const [workflows, setWorkflows] = React.useState<WorkflowType[]>([]);
  const [selected, setSelected] = React.useState<WorkflowType | null>(null);
  const [name, setName] = React.useState("");
  const [json, setJson] = React.useState("");
  const [executeResult, setExecuteResult] = React.useState<string>("");
  const [payloadJson, setPayloadJson] = React.useState('{"amount": 15000}');

  const load = async () => {
    const data = await listWorkflows();
    setWorkflows(data);
  };

  React.useEffect(() => {
    void load();
  }, []);

  const handleSelect = (wf: WorkflowType) => {
    setSelected(wf);
    setName(wf.name);
    setJson(JSON.stringify(wf.definition, null, 2));
    setExecuteResult("");
  };

  const handleNew = () => {
    setSelected(null);
    setName("新工作流");
    setJson(JSON.stringify(defaultDefinition, null, 2));
    setExecuteResult("");
  };

  const handleSave = async () => {
    try {
      const definition = JSON.parse(json) as WorkflowType["definition"];
      if (selected) {
        await updateWorkflow(selected.id, name, definition);
      } else {
        await createWorkflow(name, definition);
      }
      await load();
      setExecuteResult("保存成功");
    } catch (error) {
      setExecuteResult(`保存失败：${String(error)}`);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteWorkflow(id);
    if (selected?.id === id) {
      setSelected(null);
      setName("");
      setJson("");
    }
    await load();
  };

  const handleExecute = async () => {
    if (!selected) return;
    try {
      const payload = JSON.parse(payloadJson) as Record<string, unknown>;
      const result = await executeWorkflow(selected.id, payload);
      setExecuteResult(JSON.stringify(result, null, 2));
    } catch (error) {
      setExecuteResult(`执行失败：${String(error)}`);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">工作流</h1>
          <p className="text-sm text-muted-foreground">可视化编排业务自动化流程。</p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="mr-1.5 h-4 w-4" />
          新建工作流
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Workflow className="h-4 w-4" />
              工作流列表
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {workflows.length === 0 && <p className="text-sm text-muted-foreground">暂无工作流</p>}
            {workflows.map((wf) => (
              <div
                key={wf.id}
                className={`flex cursor-pointer items-center justify-between rounded-md border p-2 text-sm hover:bg-accent ${selected?.id === wf.id ? "bg-accent" : ""}`}
                onClick={() => handleSelect(wf)}
              >
                <span className="truncate">{wf.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDelete(wf.id);
                  }}
                >
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{selected ? "编辑工作流" : "新建工作流"}</CardTitle>
            <CardDescription>当前使用 JSON 定义节点，后续将支持可视化画布。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="工作流名称" />
            <Textarea
              value={json}
              onChange={(e) => setJson(e.target.value)}
              rows={16}
              className="font-mono text-xs"
              placeholder="工作流 JSON 定义"
            />
            <div className="flex items-center gap-2">
              <Button onClick={() => void handleSave()}>
                <Save className="mr-1.5 h-4 w-4" />
                保存
              </Button>
              {selected && (
                <Button variant="outline" onClick={() => void handleExecute()}>
                  <Play className="mr-1.5 h-4 w-4" />
                  执行
                </Button>
              )}
            </div>
            {selected && (
              <div className="space-y-2">
                <label className="text-sm font-medium">执行 Payload（JSON）</label>
                <Textarea
                  value={payloadJson}
                  onChange={(e) => setPayloadJson(e.target.value)}
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>
            )}
            {executeResult && (
              <Textarea value={executeResult} readOnly rows={8} className="font-mono text-xs" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
